import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

//Recommended by redux toolkit: https://redux-toolkit.js.org/rtk-query/usage-with-typescript#inline-error-handling-example

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(
  error: unknown
): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isErrorWithMessage(
  error: unknown
): error is { message: string } {
  return (
    typeof error === 'object' &&
    error != null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  );
}

/**
 * Checks whether an RTK Query error carries a GM API error code.
 * @param error - Error returned by an RTK Query hook
 * @param errorCode - `errorCode` to look for in the response envelope
 * @returns True when any error in the envelope matches
 */
export function hasApiErrorCode(error: unknown, errorCode: string): boolean {
  if (!isFetchBaseQueryError(error) || !('data' in error)) return false;

  const { errors } = (error.data ?? {}) as {
    errors?: Array<{ errorCode?: string }>;
  };

  return Boolean(errors?.some((apiError) => apiError.errorCode === errorCode));
}

/**
 * Normalizes an unknown error to a user-facing message (RTK Query, Error-like, or fallback).
 */
export function getRTKQueryErrorMessage(error: unknown): string {
  if (isFetchBaseQueryError(error)) {
    if ('data' in error && error.data && typeof error.data === 'object') {
      const data = error.data as { message?: string };
      if (typeof data.message === 'string') return data.message;
    }
  }
  if (isErrorWithMessage(error)) return error.message;
  return translateText('common:errors.fallback');
}
